import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  Settings,
  Type,
  Contrast,
  Volume2,
  Bell,
  RotateCcw,
  Check,
  Loader2,
  Eye,
  Users,
  User,
  UserPlus,
  Flame
} from 'lucide-react';
import { usePreferences, UserPreferences } from '../context/PreferencesContext';

export default function Preferences() {
  const { preferences, loading, updatePreferences, resetPreferences } = usePreferences();
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleChange<K extends keyof UserPreferences>(
    key: K,
    value: UserPreferences[K]
  ) {
    setSaving(true);
    setError(null);
    setSaved(false);

    try {
      await updatePreferences({ [key]: value });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError('Failed to save preference');
    } finally {
      setSaving(false);
    }
  }

  async function handleReset() {
    if (!confirm('Reset all preferences to defaults?')) return;

    setSaving(true);
    setError(null);

    try {
      await resetPreferences();
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError('Failed to reset preferences');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-accent-blue" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <Link
        to="/dashboard"
        className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-8 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Dashboard
      </Link>

      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <Settings className="w-8 h-8 text-accent-blue" />
          <h1 className="text-3xl font-bold text-white">Preferences</h1>
        </div>

        <div className="flex items-center gap-2">
          {saving && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
          {saved && <Check className="w-4 h-4 text-green-400" />}
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400">
          {error}
        </div>
      )}

      <div className="space-y-8">
        {/* Display Settings */}
        <section className="bg-gray-800/50 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
            <Type className="w-5 h-5 text-accent-blue" />
            Display Settings
          </h2>

          <div className="space-y-6">
            {/* Font Size */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-3">
                Font Size
              </label>
              <div className="grid grid-cols-4 gap-2">
                {(['small', 'medium', 'large', 'xlarge'] as const).map((size) => (
                  <button
                    key={size}
                    onClick={() => handleChange('font_size', size)}
                    className={`
                      px-4 py-2 rounded-lg text-sm font-medium transition-all
                      ${preferences.font_size === size
                        ? 'bg-accent-blue text-slate-900'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      }
                    `}
                  >
                    {size.charAt(0).toUpperCase() + size.slice(1)}
                  </button>
                ))}
              </div>
              <p className="mt-2 text-sm text-gray-500">
                Preview: <span style={{ fontSize: { small: '14px', medium: '16px', large: '18px', xlarge: '20px' }[preferences.font_size] }}>The quick brown fox</span>
              </p>
            </div>

            {/* Font Family */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-3">
                Font Style
              </label>
              <div className="grid grid-cols-3 gap-2">
                {([
                  { value: 'default', label: 'Default' },
                  { value: 'dyslexic', label: 'Dyslexia-Friendly' },
                  { value: 'monospace', label: 'Monospace' }
                ] as const).map(({ value, label }) => (
                  <button
                    key={value}
                    onClick={() => handleChange('font_family', value)}
                    className={`
                      px-4 py-2 rounded-lg text-sm font-medium transition-all
                      ${preferences.font_family === value
                        ? 'bg-accent-blue text-slate-900'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      }
                    `}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Accessibility Settings */}
        <section className="bg-gray-800/50 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
            <Eye className="w-5 h-5 text-accent-blue" />
            Accessibility
          </h2>

          <div className="space-y-4">
            {/* High Contrast */}
            <label className="flex items-center justify-between p-3 bg-gray-700/50 rounded-lg cursor-pointer hover:bg-gray-700 transition-colors">
              <div className="flex items-center gap-3">
                <Contrast className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-white font-medium">High Contrast Mode</p>
                  <p className="text-sm text-gray-400">Enhanced color contrast for better visibility</p>
                </div>
              </div>
              <input
                type="checkbox"
                checked={preferences.high_contrast}
                onChange={(e) => handleChange('high_contrast', e.target.checked)}
                className="w-5 h-5 rounded accent-accent-blue"
              />
            </label>

            {/* Reduced Motion */}
            <label className="flex items-center justify-between p-3 bg-gray-700/50 rounded-lg cursor-pointer hover:bg-gray-700 transition-colors">
              <div className="flex items-center gap-3">
                <RotateCcw className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-white font-medium">Reduced Motion</p>
                  <p className="text-sm text-gray-400">Minimize animations and transitions</p>
                </div>
              </div>
              <input
                type="checkbox"
                checked={preferences.reduced_motion}
                onChange={(e) => handleChange('reduced_motion', e.target.checked)}
                className="w-5 h-5 rounded accent-accent-blue"
              />
            </label>

            {/* Text to Speech */}
            <label className="flex items-center justify-between p-3 bg-gray-700/50 rounded-lg cursor-pointer hover:bg-gray-700 transition-colors">
              <div className="flex items-center gap-3">
                <Volume2 className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-white font-medium">Screen Reader Optimizations</p>
                  <p className="text-sm text-gray-400">Enhanced support for screen readers</p>
                </div>
              </div>
              <input
                type="checkbox"
                checked={preferences.text_to_speech}
                onChange={(e) => handleChange('text_to_speech', e.target.checked)}
                className="w-5 h-5 rounded accent-accent-blue"
              />
            </label>
          </div>
        </section>

        {/* Learning Settings */}
        <section className="bg-gray-800/50 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
            <Bell className="w-5 h-5 text-accent-blue" />
            Learning Settings
          </h2>

          <div className="space-y-6">
            {/* Break Reminders */}
            <label className="flex items-center justify-between p-3 bg-gray-700/50 rounded-lg cursor-pointer hover:bg-gray-700 transition-colors">
              <div>
                <p className="text-white font-medium">Break Reminders</p>
                <p className="text-sm text-gray-400">Periodic reminders to take breaks during lessons</p>
              </div>
              <input
                type="checkbox"
                checked={preferences.lesson_break_reminders}
                onChange={(e) => handleChange('lesson_break_reminders', e.target.checked)}
                className="w-5 h-5 rounded accent-accent-blue"
              />
            </label>

            {/* Streak At-Risk Reminders */}
            <label className="flex items-center justify-between p-3 bg-gray-700/50 rounded-lg cursor-pointer hover:bg-gray-700 transition-colors">
              <div className="flex items-center gap-3">
                <Flame className="w-5 h-5 text-orange-400" />
                <div>
                  <p className="text-white font-medium">Streak Reminders</p>
                  <p className="text-sm text-gray-400">Show a reminder when your streak is about to break</p>
                </div>
              </div>
              <input
                type="checkbox"
                checked={preferences.streak_reminder_enabled}
                onChange={(e) => handleChange('streak_reminder_enabled', e.target.checked)}
                className="w-5 h-5 rounded accent-accent-blue"
              />
            </label>

            {/* Playback Speed */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-3">
                Media Playback Speed
              </label>
              <div className="flex items-center gap-4">
                <input
                  type="range"
                  min="0.5"
                  max="2"
                  step="0.25"
                  value={preferences.playback_speed}
                  onChange={(e) => handleChange('playback_speed', parseFloat(e.target.value))}
                  className="flex-1 accent-accent-blue"
                />
                <span className="text-white font-medium w-12 text-right">
                  {preferences.playback_speed}x
                </span>
              </div>
            </div>

            {/* Notification Frequency */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-3">
                Notification Frequency
              </label>
              <div className="grid grid-cols-4 gap-2">
                {([
                  { value: 'off', label: 'Off' },
                  { value: 'minimal', label: 'Minimal' },
                  { value: 'normal', label: 'Normal' },
                  { value: 'frequent', label: 'Frequent' }
                ] as const).map(({ value, label }) => (
                  <button
                    key={value}
                    onClick={() => handleChange('notification_frequency', value)}
                    className={`
                      px-4 py-2 rounded-lg text-sm font-medium transition-all
                      ${preferences.notification_frequency === value
                        ? 'bg-accent-blue text-slate-900'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      }
                    `}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Social & Connection Settings */}
        <section className="bg-gray-800/50 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
            <Users className="w-5 h-5 text-accent-blue" />
            Social & Connection
          </h2>

          <div className="space-y-4">
            <p className="text-gray-400 text-sm mb-4">
              Choose how you want to connect with other learners. You can change this anytime.
            </p>

            {/* Solo Mode */}
            <label className="flex items-start gap-4 p-4 bg-gray-700/50 rounded-lg cursor-pointer hover:bg-gray-700 transition-colors">
              <input
                type="radio"
                name="connection_mode"
                value="solo"
                checked={preferences.connection_mode === 'solo'}
                onChange={() => handleChange('connection_mode', 'solo')}
                className="mt-1 w-4 h-4 accent-accent-blue"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <User className="w-5 h-5 text-gray-400" />
                  <p className="text-white font-medium">Solo</p>
                </div>
                <p className="text-sm text-gray-400 mt-1">
                  Just me and my progress. No social features.
                </p>
              </div>
            </label>

            {/* Silent Cohort Mode */}
            <label className="flex items-start gap-4 p-4 bg-gray-700/50 rounded-lg cursor-pointer hover:bg-gray-700 transition-colors">
              <input
                type="radio"
                name="connection_mode"
                value="silent_cohort"
                checked={preferences.connection_mode === 'silent_cohort'}
                onChange={() => handleChange('connection_mode', 'silent_cohort')}
                className="mt-1 w-4 h-4 accent-accent-blue"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-gray-400" />
                  <p className="text-white font-medium">Silent Cohort</p>
                </div>
                <p className="text-sm text-gray-400 mt-1">
                  See anonymous activity from others in your track. No names, just the feeling of learning together.
                </p>
              </div>
            </label>

            {/* Accountability Buddy Mode */}
            <label className="flex items-start gap-4 p-4 bg-gray-700/50 rounded-lg cursor-pointer hover:bg-gray-700 transition-colors">
              <input
                type="radio"
                name="connection_mode"
                value="buddy"
                checked={preferences.connection_mode === 'buddy'}
                onChange={() => handleChange('connection_mode', 'buddy')}
                className="mt-1 w-4 h-4 accent-accent-blue"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <UserPlus className="w-5 h-5 text-gray-400" />
                  <p className="text-white font-medium">Accountability Buddy</p>
                </div>
                <p className="text-sm text-gray-400 mt-1">
                  Get matched with another learner in your track. Exchange async messages and weekly check-ins.
                </p>
              </div>
            </label>

            {/* Buddy status indicator (shown when in buddy mode) */}
            {preferences.connection_mode === 'buddy' && (
              <div className="mt-4 p-4 bg-accent-blue/10 border border-accent-blue/30 rounded-lg">
                <p className="text-accent-blue text-sm">
                  Visit your Dashboard to see your buddy status and messages.
                </p>
              </div>
            )}
          </div>
        </section>

        {/* Reset */}
        <div className="flex justify-end">
          <button
            onClick={handleReset}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 text-gray-400 hover:text-white transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
            Reset to Defaults
          </button>
        </div>
      </div>
    </div>
  );
}
